import PropTypes from 'prop-types';
import React, { useState, useEffect, useCallback } from 'react';

/* istanbul ignore next */
const InlineFilePreview = ({ file, onClose, size, preferredMaxHeight = 300 }) => {
    const [loading, setLoading] = useState(true);
    const [csvRows, setCsvRows] = useState([]);

    const getFileType = (fileName) => {
        const extension = fileName.split('.').pop()?.toLowerCase();
        return extension;
    };

    // Helper function to create stable keys from content
    const createStableKey = (content, prefix = '', fallback = '') => {
        const cleanContent = String(content || fallback).replace(/[^a-zA-Z0-9]/g, '');
        return `${prefix}-${cleanContent.substring(0, 30) || 'empty'}-${cleanContent.length}`;
    };

    const parseCsvData = (csvText) => {
        const lines = csvText.split('\n');
        const rows = lines.map(line => {
            // Simple CSV parsing - handles basic cases
            return line.split(',').map(cell => cell.trim().replace(/^"$/g, ''));
        }).filter(row => row.some(cell => cell.length > 0)); // Remove empty rows
        return rows;
    };

    const loadCsvContent = useCallback(async (fileUrl) => {
        try {
            const response = await fetch(fileUrl);
            const text = await response.text();
            setCsvRows(parseCsvData(text));
            setLoading(false);
        } catch (error) {
            console.error('Error loading CSV:', error);
            setLoading(false);
        }
    }, []);

    const readFileContent = useCallback((file) => {
        const reader = new FileReader();
        reader.onload = (e) => {
            const text = e.target.result;
            setCsvRows(parseCsvData(text));
            setLoading(false);
        };
        reader.onerror = () => {
            setLoading(false);
        };
        reader.readAsText(file);
    }, []);

    useEffect(() => {
        if (!file) return;

        const fileType = getFileType(file.name);
        if (fileType === 'csv') {
            if (file.fileUrl) {
                loadCsvContent(file.fileUrl);
            } else if (file instanceof File) {
                readFileContent(file);
            }
        } else {
            // For non-CSV files, set loading to false immediately
            setLoading(false);
        }
    }, [file, loadCsvContent, readFileContent]);

    const renderPreview = () => {
        if (!file) return null;

        const fileType = getFileType(file.name);
        const fileUrl = file.fileUrl || URL.createObjectURL(file);

        switch (fileType) {
            case 'pdf':
                return (
                    <iframe
                        src={fileUrl}
                        width="100%"
                        height={`${preferredMaxHeight}px`}
                        title="PDF Preview"
                        onLoad={() => setLoading(false)}
                        style={{ border: '1px solid #ddd', borderRadius: '4px' }}
                    />
                );
            case 'jpg':
            case 'jpeg':
            case 'png':
            case 'gif':
            case 'bmp':
                return (
                    <img
                        src={fileUrl}
                        alt="File Preview"
                        style={{
                            maxWidth: '100%',
                            maxHeight: `${preferredMaxHeight}px`,
                            border: '1px solid #ddd',
                            borderRadius: '4px'
                        }}
                        onLoad={() => setLoading(false)}
                        onError={() => setLoading(false)}
                    />
                );
            case 'txt':
                return (
                    <iframe
                        src={fileUrl}
                        width="100%"
                        height={`${preferredMaxHeight}px`}
                        title="Text Preview"
                        onLoad={() => setLoading(false)}
                        style={{ border: '1px solid #ddd', borderRadius: '4px' }}
                    />
                );
            case 'csv':
                return (
                    <div style={{ border: '1px solid #ddd', borderRadius: '4px', maxHeight: preferredMaxHeight, overflow: 'auto', maxWidth: '100%'  }}>
                        {csvRows.length > 0 ? (
                            <table className="table table-striped table-bordered mb-0" style={{ fontSize: '12px' }}>
                                <thead className="thead-light">
                                    <tr>
                                        {csvRows[0]?.map((header, idx) => (
                                            <th key={createStableKey(header, 'header') + '-' + idx} style={{ padding: '8px', whiteSpace: 'nowrap' }}>
                                                {header}
                                            </th>
                                        ))}
                                    </tr>
                                </thead>
                                <tbody>
                                    {csvRows.slice(1, 21).map((row, rowIndex) => ( // Show max 20 rows
                                        <tr key={createStableKey(row.join('|'), 'row') + '-' + rowIndex}>
                                            {row.map((cell, cellIndex) => (
                                                <td key={createStableKey(cell, 'cell', `${cellIndex}-${rowIndex}`) + '-' + cellIndex} style={{ padding: '8px', whiteSpace: 'nowrap' }}>
                                                    {cell}
                                                </td>
                                            ))}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        ) : (
                            <div className="text-center p-3">
                                <p>No data found in CSV file</p>
                            </div>
                        )}
                        {csvRows.length > 21 && (
                            <div className="text-center p-2 bg-light">
                                <small className="text-muted">
                                    Showing first 20 rows of {csvRows.length - 1} total rows
                                </small>
                            </div>
                        )}
                    </div>
                );
            case 'xlsx':
            case 'xls':
                return (
                    <div className="text-center p-4" style={{ border: '1px solid #ddd', borderRadius: '4px', backgroundColor: '#f8f9fa' }}>
                        <i className="fa fa-file-excel-o fa-3x mb-3" style={{ color: '#28a745' }}></i>
                        <p><strong>Excel File</strong></p>
                        <p className="text-muted">File: {file.name}</p>
                        <p className="text-muted">Excel preview not available. Please download to view the content.</p>
                        {file.fileUrl && (
                            <a href={file.fileUrl} target="_blank" rel="noreferrer" className="btn btn-sm btn-success">
                                <i className="fa fa-download"></i> Download File
                            </a>
                        )}
                    </div>
                );
            case 'doc':
            case 'docx':
                return (
                    <div className="text-center p-4" style={{ border: '1px solid #ddd', borderRadius: '4px', backgroundColor: '#f8f9fa' }}>
                        <i className="fa fa-file-word-o fa-3x mb-3" style={{ color: '#007bff' }}></i>
                        <p><strong>Word Document</strong></p>
                        <p className="text-muted">File: {file.name}</p>
                        <p className="text-muted">Word document preview not available. Please download to view the content.</p>
                        {file.fileUrl && (
                            <a href={file.fileUrl} target="_blank" rel="noreferrer" className="btn btn-sm btn-primary">
                                <i className="fa fa-download"></i> Download File
                            </a>
                        )}
                    </div>
                );
            default:
                return (
                    <div className="text-center p-4" style={{ border: '1px solid #ddd', borderRadius: '4px', backgroundColor: '#f8f9fa' }}>
                        <i className="fa fa-file fa-3x mb-3" style={{ color: '#ccc' }}></i>
                        <p>Preview not available for this file type.</p>
                        <p className="text-muted">File: {file.name}</p>
                        {file.fileUrl && (
                            <a href={file.fileUrl} target="_blank" rel="noreferrer" className="btn btn-sm btn-secondary">
                                <i className="fa fa-download"></i> Download File
                            </a>
                        )}
                    </div>
                );
        }
    };

    return (
        <div className="file-preview-container">
            
            {loading && (
                <div className="text-center p-3">
                    <i className="fa fa-spinner fa-spin fa-2x"></i>
                    <p className="mt-2">Loading preview...</p>
                </div>
            )}
            <div className={loading ? 'd-none' : ''}>
                {renderPreview()}
            </div>
        </div>
    );
};
InlineFilePreview.propTypes = {
    file: PropTypes.oneOfType([
        PropTypes.instanceOf(File),
        PropTypes.shape({
            name: PropTypes.string.isRequired,
            type: PropTypes.string.isRequired,
            size: PropTypes.number.isRequired,
            fileUrl: PropTypes.string
        })
    ]).isRequired,
    onClose: PropTypes.func.isRequired,
    size: PropTypes.oneOf(['small', 'medium', 'large']),
    preferredMaxHeight: PropTypes.string
};

export default InlineFilePreview;